import React, { FC } from "react";
import { gql } from "@apollo/client";
import { css } from "linaria";

import LazyContent from "../LazyContent";

import useMobile from "../../hooks/useMobile";

const styles = {
  header: css`
    padding-bottom: 8.53%;
    @screen lg {
      padding-bottom: 3.88%;
    }
  `,
  pageStyle: css`
    padding-bottom: 100%;
    @screen lg {
      padding-bottom: 50%;
    }
  `,
  media: css`
    padding-bottom: 100%;
    @screen lg {
      padding-bottom: 50%;
    }
  `,
};

interface Props {
  composition: any;
  priority?: boolean;
}

const Composition: FC<Props> = ({ composition, priority = false }) => {
  const isMobile = useMobile();
  const name = composition.family.meta.label;
  const headerColor = composition.headerColor;

  const pageStyleUrl = isMobile
    ? composition.artifacts.pageStyleMobile.asset.renditions.thumbMedium.url
    : composition.artifacts.pageStyleDesktop.asset.renditions.thumbLarge.url;

  const mediaUrl = isMobile
    ? composition.artifacts.media.asset.renditions.mp4Small.url
    : composition.artifacts.media.asset.renditions.mp4Large.url;

  const mediaPosterUrl = isMobile
    ? composition.artifacts.media.asset.renditions.jpegSmall.url
    : composition.artifacts.media.asset.renditions.jpegLarge.url;

  return (
    <div className="flex flex-col w-full rounded lg:rounded-none overflow-hidden shadow">
      <div className={styles.header} style={{ backgroundColor: headerColor }} />
      <div className="flex flex-col-reverse lg:flex-row">
        <LazyContent
          className={`flex flex-col justify-end relative w-full ${styles.pageStyle}`}
          priority={priority}
        >
          <img
            src={pageStyleUrl}
            alt={name}
            className="absolute inset-x-0 inset-y-0 w-full h-auto"
            // @ts-expect-error
            importance={priority ? "high" : "auto"}
          />
        </LazyContent>
        <LazyContent
          className={`flex flex-col justify-end relative w-full ${styles.media}`}
          priority={priority}
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            crossOrigin="anonymous"
            x-webkit-airplay="deny"
            src={mediaUrl}
            poster={mediaPosterUrl}
            className="absolute inset-x-0 inset-y-0 w-full h-auto"
            disableRemotePlayback={true}
          />
        </LazyContent>
      </div>
    </div>
  );
};

// TODO: CompositionPLPFragments and CompositionPDPFragments vary only on the type that they are "on"
// Is there a way to make this not care about that difference?
const CompositionPLPFragments = gql`
  fragment CompositionFragment on CompositionSearchResult {
    family {
      meta
    }
    headerColor
    artifacts {
      pageStyleDesktop {
        asset {
          renditions(renditionTypes: ["thumbLarge"])
        }
      }
      pageStyleMobile {
        asset {
          renditions(renditionTypes: ["thumbMedium"])
        }
      }
      media {
        asset {
          renditions(
            renditionTypes: ["mp4Large", "mp4Small", "jpegLarge", "jpegSmall"]
          )
        }
      }
    }
  }
`;

const CompositionPDPFragments = gql`
  fragment CompositionFragment on FlyerCompositionDetail {
    family {
      meta
    }
    headerColor
    artifacts {
      pageStyleDesktop {
        asset {
          renditions(renditionTypes: ["thumbLarge"])
        }
      }
      pageStyleMobile {
        asset {
          renditions(renditionTypes: ["thumbMedium"])
        }
      }
      media {
        asset {
          renditions(
            renditionTypes: ["mp4Large", "mp4Small", "jpegLarge", "jpegSmall"]
          )
        }
      }
    }
  }
`;

export {
  CompositionPLPFragments,
  CompositionPDPFragments,
}

export default React.memo(Composition);
